import React, { useRef, useState } from 'react';
import { Descriptions, Tag, Typography, message } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import './styles.less';
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import { useDeleteLeaderboardMutation, useLeaderboardTypesQuery } from '../../gql/hooks/useLeaderboardQueries';
import { ColoredTag, ViewPageActions } from '../GenericCreateEdit/GenericElements';

dayjs.extend( localizedFormat );

const ViewLeaderboardsPage = () => {

    const listPageRef = useRef();

    const leaderboardTypes = useLeaderboardTypesQuery();
    const [ deleteLeaderboardMutation ] = useDeleteLeaderboardMutation();

    const [ messageApi ] = message.useMessage();

    const [ , setExpandedRows ] = useState( [] );
    const [ deleting, setDeleting ] = useState( false );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Incentives', path: Routes.GamificationNav.buildPath() },
        { name: 'Leaderboards' }
    ];
    
    const handleEdit = ( record ) => {
        routeHistory.push( Routes.EditLeaderboard.buildPath( record.id ) );
    };

    const handleDelete = async ( record ) => {
        setDeleting( true );
        messageApi.open( {
            type: 'loading',
            content: 'Deleting leaderboard...',
            duration: 0,
        } );

        try {
            await deleteLeaderboardMutation( record.id );
            messageApi.destroy();
            messageApi.success( 'Leaderboard successfully deleted!' );
        } catch ( e ) {
            messageApi.destroy();
            messageApi.error( 'Could not delete leaderboard' );
            console.error( e );
            setDeleting( false );
            return;
        }

        setTimeout( () => {
            setDeleting( false );
            listPageRef.current?.reload();
        }, 1500 );
    };

    const renderExpandedRow = ( item ) => {
        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <Descriptions bordered layout='vertical' items={ [
                            {
                                key: 'desc',
                                label: `Description (${ DEFAULT_LOCALE_CODE })`,
                                children: item.description?.length > 0 ?
                                    <Typography.Paragraph style={ { marginTop: 8 } }>
                                        { item.description }
                                    </Typography.Paragraph> :
                                    <span className='canceled'>No description</span>
                            }, {
                                key: 'gran',
                                label: 'Granularities',
                                className: 'tag-list',
                                children: item.granularities.map( g => <Tag key={ g.key } color={ g.color }>{ g.name }</Tag> ) 
                            }, {
                                key: 'audiences',
                                label: 'Audiences',
                                className: 'tag-list',
                                children: item.audiences?.length > 0 ? 
                                    <>{ item.audiences.map( a => <ColoredTag key={ a.id } value='audience'>{ a.name }</ColoredTag> ) }</> :
                                    <span className='canceled'>No audiences defined</span>
                            }, {
                                key: 'range',
                                label: 'Time Range',
                                children: <>
                                    <span>
                                        <b>Start: </b>
                                        { item.startAt ? <span>{ item.startAt.tz( item.timezone ?? dayjs.tz.guess() ).format( 'LLL z' ) }</span> : <span className='canceled'>No start time</span> }
                                    </span>
                                    <br />
                                    <span>
                                        <b>End: </b>
                                        { item.endAt ? <span>{ item.endAt.tz( item.timezone ?? dayjs.tz.guess() ).format( 'LLL z' ) }</span> : <span className='canceled'>No end time</span> }
                                    </span>
                                </>
                            },
                            
                        ] } />
                    </div>
                </div>
            </div>
        );
    };

    const tableColumns = [
        {
            dataIndex: 'name',
            sortKey: 'name',
            key: 'name',
            title: 'Name',
            fixed: 'left',
        },
        {
            dataIndex: 'leaderboardType',
            key: 'type',
            title: 'Type',
            render: type => <ColoredTag value={ leaderboardTypes?.find( t => t.label === type )?.name } />
        },
        {
            dataIndex: 'granularity',
            key: 'granularity',
            title: 'Granularity',
            render: ( gran, { granularities } ) => {
                const tag = granularities.find( g => g.key === gran );
                return <Tag color={ tag.color }>{ tag.name }</Tag>;
            },
        },
        {
            dataIndex: 'dateRange',
            key: 'range',
            title: <div>
                <span>Start</span>
                <span>-</span>
                <span>End</span>
            </div>,
            className: 'date-range',
            render: ( _, item ) => <div>
                { item.startAt ? <span>{ item.startAt.format( 'l' ) }</span> : <span className='canceled'>&mdash;</span> }
                <span>{ item.startAt && item.endAt ? '-' : '' }</span>
                { item.endAt ? <span>{ item.endAt.format( 'l' ) }</span> : <span className='canceled'>&mdash;</span> }
            </div>
        },
        {
            title: 'Published',
            key: 'isPublished',
            dataIndex: 'isPublished',
            render: val => val ? <Tag color='green' icon={ <EyeOutlined /> }>Published</Tag> : <Tag color='red' icon={ <EyeInvisibleOutlined /> }>Unpublished</Tag>
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sortKey: 'createdAt',
            render: val => val?.format( 'lll' )
        },
        {
            title: 'Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sortKey: 'updatedAt',
            render: val => val?.format( 'lll' )
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            align: 'center',
            width: '100px',
            render: ( item ) => <ViewPageActions item={ item } handleEditCb={ handleEdit } handleDeleteCb={ handleDelete } />
        }
    ];

    function setExpandedRowsCb( rows ) {

        setExpandedRows( rows );
    }

    return <SearchQueryList
        ref={ listPageRef }
        type='leaderboards'
        uiStrings={ {
            cardTitle: 'Leaderboards',
            createButtonTitle: 'Create Leaderboard'
        } }
        tableColumns={ tableColumns }
        breadcrumbs={ breadcrumbs }
        isDeleting={ deleting }
        createClickCallback={ () => routeHistory.push( Routes.CreateLeaderboard.buildPath() ) }
        renderExpandedRowCallback={ renderExpandedRow }
        setExpandedRowsCallback={ setExpandedRowsCb }
        defaultExpandedRows={ [] }
        />;
};

export default ViewLeaderboardsPage;
